/**
 * FITROX ACTIVEWEAR PDP SCROLL-TIED & INTERACTIVE ENGINE
 * - Sticky scroll-tied sub-navigation rail with IntersectionObserver spy
 * - Smooth scroll jump to sections
 * - In-buy-box injection (Amazon Express shipping timer, trust strip, size guide trigger)
 * - Interactive Size Guide modal & tabs
 * - Floating Sticky Add to Cart bar with variant sync and AJAX add-to-cart
 */

(() => {
  'use strict';

  function initFitroxPdp() {
    initSubnav();
    initBuyBoxAddons();
    initStockStatusWatcher();
    initAccordions();
    initPostcodeChecker();
    initCountdown();
    initSizeModals();
    initInlineSizingTabs();
    initStickyAtc();
  }

  /* ------------------------------------------------------------
     1. STICKY SCROLL-TIED SUBNAV & SCROLL SPY
     ------------------------------------------------------------ */
  function initSubnav() {
    const subnav = document.getElementById('FitroxPdpSubnav');
    const mainContent = document.getElementById('MainContent');
    if (!subnav || !mainContent) return;

    // Place subnav at the top of #MainContent if not already there
    if (subnav.parentElement !== mainContent || mainContent.firstElementChild !== subnav) {
      mainContent.insertBefore(subnav, mainContent.firstChild);
    }

    // Ensure main product area has id="pdp-overview" for anchor navigation
    const mainProd = document.querySelector('[data-testid="product-information"]') ||
                     document.querySelector('.product-information') ||
                     document.getElementById('ProductInformation-template--28512830980377__main');
    if (mainProd && !mainProd.id) {
      mainProd.id = 'pdp-overview';
    } else if (mainProd && mainProd.id !== 'pdp-overview') {
      mainProd.setAttribute('data-scroll-anchor', 'pdp-overview');
    }

    const pills = subnav.querySelectorAll('[data-pdp-tab]');
    const sectionIds = ['pdp-overview', 'FitroxAccordionSizing', 'FitroxAccordionDelivery', 'pdp-recommendations'];

    // Smooth scroll on click
    pills.forEach(pill => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        const tabId = pill.dataset.pdpTab;
        let target = document.getElementById(tabId);
        if (!target && tabId === 'pdp-overview') {
          target = mainProd || mainContent;
        }

        if (tabId === 'FitroxAccordionSizing' || tabId === 'pdp-sizing') {
          const acc = document.getElementById('FitroxAccordionSizing') || document.getElementById('NexusAccSizing');
          if (acc) {
            acc.open = true;
            const icon = acc.querySelector('.fitrox-nexus-icon, .fitrox-pdp-accordion__icon');
            if (icon) icon.textContent = '−';
            target = acc;
          }
        } else if (tabId === 'FitroxAccordionDelivery' || tabId === 'pdp-delivery') {
          const acc = document.getElementById('FitroxAccordionDelivery') || document.getElementById('NexusAccDelivery');
          if (acc) {
            acc.open = true;
            const icon = acc.querySelector('.fitrox-nexus-icon, .fitrox-pdp-accordion__icon');
            if (icon) icon.textContent = '−';
            target = acc;
          }
        }

        if (target) {
          const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height') || '60', 10);
          const subnavHeight = subnav.offsetHeight || 52;
          const targetY = target.getBoundingClientRect().top + window.pageYOffset - (headerHeight + subnavHeight + 10);

          window.scrollTo({
            top: Math.max(0, targetY),
            behavior: 'smooth'
          });

          pills.forEach(p => p.classList.remove('is-active'));
          pill.classList.add('is-active');
        }
      });
    });

    // Scroll-tied spy using IntersectionObserver
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id || entry.target.getAttribute('data-scroll-anchor');
          if (id) {
            pills.forEach(pill => {
              if (pill.dataset.pdpTab === id) {
                pill.classList.add('is-active');
                // Auto scroll subnav horizontally to keep active pill visible
                pill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
              } else {
                pill.classList.remove('is-active');
              }
            });
          }
        }
      });
    }, observerOptions);

    sectionIds.forEach(id => {
      const el = document.getElementById(id) || (id === 'pdp-overview' ? mainProd : null);
      if (el) sectionObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------
     2. IN-BUY-BOX INJECTION
     ------------------------------------------------------------ */
  function initBuyBoxAddons() {
    const addons = document.getElementById('FitroxBuyBoxAddons');
    if (!addons) return;

    const sizeTrigger = document.getElementById('FitroxBuyBoxSizeTrigger');
    const productInfo = document.querySelector('[data-testid="product-information"]') || document.querySelector('.product-details');
    if (!productInfo) return;

    // Find Size option fieldset to place the size button directly above size pills
    const fieldsets = productInfo.querySelectorAll('fieldset.variant-option');
    let sizeFieldset = null;
    fieldsets.forEach(fs => {
      const legend = fs.querySelector('legend');
      if (legend && /\bsize\b/i.test(legend.textContent)) {
        sizeFieldset = fs;
      }
    });

    if (sizeFieldset && sizeTrigger) {
      const legend = sizeFieldset.querySelector('legend');
      if (legend && sizeTrigger.parentElement !== sizeFieldset) {
        legend.insertAdjacentElement('afterend', sizeTrigger);
      }
    }

    // Move Rating and In-Stock strip directly below the price
    const ratingStrip = document.getElementById('FitroxBuyBoxRatingStrip');
    const priceEl = productInfo.querySelector('.price') ||
                    productInfo.querySelector('[data-testid="price"]') ||
                    productInfo.querySelector('.product-price');
    if (priceEl && ratingStrip && ratingStrip.parentElement !== priceEl.parentElement) {
      priceEl.insertAdjacentElement('afterend', ratingStrip);
      ratingStrip.style.display = 'flex';
    } else if (ratingStrip) {
      ratingStrip.style.display = 'flex';
    }

    // Old addons container is not placed below buy buttons (FitroxNexusCollapsibles renders natively via Liquid SSR)
    addons.style.display = 'none';
  }

  /* ------------------------------------------------------------
     2A. DYNAMIC IN-STOCK / SOLD OUT STATUS WATCHER
     ------------------------------------------------------------ */
  function initStockStatusWatcher() {
    const stockEl = document.querySelector('#FitroxBuyBoxRatingStrip .fitrox-stock-status');
    if (!stockEl) return;

    function updateStock() {
      const atcBtn = document.querySelector('[data-testid="product-information"] button[name="add"], .product-form-buttons button, button[name="add"]');
      const isSoldOut = atcBtn && (atcBtn.disabled || /sold\s*out/i.test(atcBtn.textContent));

      if (isSoldOut) {
        stockEl.innerHTML = '<span class="fitrox-stock-status__dot is-soldout"></span><span>Sold Out (Restocking Soon)</span>';
        stockEl.classList.add('is-soldout');
      } else {
        stockEl.innerHTML = '<span class="fitrox-stock-status__dot"></span><span>In Stock (UK Warehouse)</span>';
        stockEl.classList.remove('is-soldout');
      }
    }

    // Initial check
    updateStock();

    // Listen for form variant changes
    document.addEventListener('change', (e) => {
      if (e.target.closest('.variant-option') || e.target.matches('input[data-variant-id], select[name="id"]')) {
        setTimeout(updateStock, 50);
        setTimeout(updateStock, 200);
      }
    });

    // Observe changes to ATC button state
    const atcBtn = document.querySelector('[data-testid="product-information"] button[name="add"], .product-form-buttons button, button[name="add"]');
    if (atcBtn) {
      const observer = new MutationObserver(() => {
        updateStock();
      });
      observer.observe(atcBtn, { attributes: true, childList: true, characterData: true, subtree: true });
    }
  }

  /* ------------------------------------------------------------
     2B. COLLAPSIBLE ACCORDIONS (+ / - TOGGLE)
     ------------------------------------------------------------ */
  function initAccordions() {
    const accordions = document.querySelectorAll('.fitrox-pdp-accordion');
    accordions.forEach(acc => {
      acc.addEventListener('toggle', () => {
        const icon = acc.querySelector('.fitrox-pdp-accordion__icon');
        if (icon) {
          icon.textContent = acc.open ? '−' : '+';
        }
      });
    });
  }

  /* ------------------------------------------------------------
     2C. INTERACTIVE UK POSTCODE DELIVERY ESTIMATOR
     ------------------------------------------------------------ */
  function initPostcodeChecker() {
    const input = document.getElementById('FitroxPostcodeInput');
    const btn = document.getElementById('FitroxPostcodeBtn');
    const result = document.getElementById('FitroxPostcodeResult');
    if (!input || !btn || !result) return;

    function calculateEstimate(postcode) {
      const clean = postcode.trim().toUpperCase();
      if (clean.length < 2) {
        result.className = 'fitrox-postcode-result is-error';
        result.innerHTML = '⚠️ Please enter a valid UK postcode.';
        result.style.display = 'block';
        return;
      }

      // Calculate dispatch and arrival
      const now = new Date();
      const cutoffHour = 17; // 5 PM
      let dispatchDay = new Date(now);
      let arrivesDays = 2; // Amazon 1-2 day express

      if (now.getHours() >= cutoffHour) {
        dispatchDay.setDate(dispatchDay.getDate() + 1);
      }

      const arrivalDate = new Date(dispatchDay);
      arrivalDate.setDate(arrivalDate.getDate() + arrivesDays);
      
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const arrivalDayName = dayNames[arrivalDate.getDay()];
      const arrivalDayNum = arrivalDate.getDate();
      const arrivalMonth = monthNames[arrivalDate.getMonth()];

      const dispatchText = (now.getHours() < cutoffHour) ? 'Dispatched TODAY' : 'Dispatched TOMORROW MORNING';

      result.className = 'fitrox-postcode-result';
      result.innerHTML = `<strong>✓ Free Express Amazon Delivery to ${clean}:</strong> ${dispatchText}. Estimated arrival by <strong>${arrivalDayName}, ${arrivalDayNum} ${arrivalMonth}</strong> with full courier tracking.`;
      result.style.display = 'block';

      try {
        localStorage.setItem('fitrox_user_postcode', clean);
      } catch(e) {}
    }

    btn.addEventListener('click', () => {
      calculateEstimate(input.value);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        calculateEstimate(input.value);
      }
    });

    // Restore saved postcode if present
    try {
      const saved = localStorage.getItem('fitrox_user_postcode');
      if (saved) {
        input.value = saved;
        calculateEstimate(saved);
      }
    } catch(e) {}
  }

  /* ------------------------------------------------------------
     3. DYNAMIC AMAZON EXPRESS DISPATCH TIMER
     ------------------------------------------------------------ */
  function initCountdown() {
    const timerEl = document.getElementById('FitroxCountdownTimer');
    if (!timerEl) return;

    function updateTimer() {
      const now = new Date();
      // Cutoff time 17:00 (5 PM UK time)
      const cutoff = new Date();
      cutoff.setHours(17, 0, 0, 0);

      let diff = cutoff.getTime() - now.getTime();
      if (diff <= 0) {
        // If after 5 PM, countdown to 5 PM next business day
        cutoff.setDate(cutoff.getDate() + 1);
        diff = cutoff.getTime() - now.getTime();
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      timerEl.textContent = `${hours}h ${minutes}m ${seconds}s`;
    }

    updateTimer();
    setInterval(updateTimer, 1000);
  }

  /* ------------------------------------------------------------
     4. INTERACTIVE SIZE GUIDE MODAL
     ------------------------------------------------------------ */
  function initSizeModals() {
    const modal = document.getElementById('FitroxPdpSizeModal');
    const openBtn = document.getElementById('FitroxBuyBoxSizeTrigger');
    const closeBtn = document.getElementById('FitroxModalClose');
    if (!modal) return;

    let lastFocused = null;

    function openModal() {
      lastFocused = document.activeElement;
      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
    }

    function closeModal() {
      if (typeof modal.close === 'function') {
        modal.close();
      } else {
        modal.removeAttribute('open');
      }
      if (lastFocused) lastFocused.focus();
    }

    if (openBtn) openBtn.addEventListener('click', openModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // Close on Esc
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal();
    });

    // Modal Tabs (Men / Kids)
    const modalTabs = modal.querySelectorAll('[data-modal-tab]');
    const modalPanels = modal.querySelectorAll('[data-modal-panel]');

    modalTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const targetAudience = tab.dataset.modalTab;
        modalTabs.forEach(t => t.classList.toggle('is-active', t === tab));
        modalPanels.forEach(panel => {
          panel.classList.toggle('is-hidden', panel.dataset.modalPanel !== targetAudience);
        });
      });
    });
  }

  /* ------------------------------------------------------------
     5. INLINE SIZING TABS (#pdp-sizing)
     ------------------------------------------------------------ */
  function initInlineSizingTabs() {
    const tabs = document.querySelectorAll('[data-sizing-tab]');
    const panelMen = document.getElementById('PanelMenSizing');
    const panelKids = document.getElementById('PanelKidsSizing');

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const audience = tab.dataset.sizingTab;
        tabs.forEach(t => {
          const isActive = t === tab;
          t.classList.toggle('is-active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        if (audience === 'men') {
          panelMen?.classList.remove('is-hidden');
          panelKids?.classList.add('is-hidden');
        } else {
          panelMen?.classList.add('is-hidden');
          panelKids?.classList.remove('is-hidden');
        }
      });
    });
  }

  /* ------------------------------------------------------------
     6. FLOATING STICKY ADD TO CART (Sticky ATC)
     ------------------------------------------------------------ */
  function initStickyAtc() {
    const stickyBar = document.getElementById('FitroxStickyAtcBar');
    const select = document.getElementById('FitroxStickyVariantSelect');
    const submitBtn = document.getElementById('FitroxStickySubmitBtn');
    const priceDisplay = document.getElementById('FitroxStickyPrice');
    if (!stickyBar || !submitBtn) return;

    // Observe main buy button to reveal sticky bar when it scrolls out of view
    const mainBtn = document.querySelector('[data-testid="product-information"] button[name="add"]') ||
                    document.querySelector('.product-form-buttons button') ||
                    document.querySelector('form[action*="/cart/add"] button[type="submit"]');

    if (mainBtn) {
      const atcObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            stickyBar.classList.add('is-visible');
            stickyBar.setAttribute('aria-hidden', 'false');
          } else {
            stickyBar.classList.remove('is-visible');
            stickyBar.setAttribute('aria-hidden', 'true');
          }
        });
      }, { threshold: 0 });

      atcObserver.observe(mainBtn);
    }

    // Synchronize variant change between sticky bar and main form
    if (select) {
      select.addEventListener('change', () => {
        const variantId = select.value;
        const mainInput = document.querySelector(`input[data-variant-id="${variantId}"], input[value="${variantId}"]`);
        if (mainInput) {
          mainInput.checked = true;
          mainInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Update displayed price from option text
        const selectedOpt = select.options[select.selectedIndex];
        if (selectedOpt && priceDisplay) {
          const match = selectedOpt.textContent.match(/£[\d.]+/);
          if (match) priceDisplay.textContent = match[0];
        }
      });

      // Listen for main variant picker changes to update sticky dropdown
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[data-variant-id]')) {
          const id = e.target.dataset.variantId;
          if (id && select.value !== id) {
            select.value = id;
            const opt = select.querySelector(`option[value="${id}"]`);
            if (opt && priceDisplay) {
              const match = opt.textContent.match(/£[\d.]+/);
              if (match) priceDisplay.textContent = match[0];
            }
          }
        }
      });
    }

    // Handle AJAX Add to Cart from Sticky Bar
    submitBtn.addEventListener('click', async () => {
      const variantId = select ? select.value : submitBtn.dataset.variantId;
      if (!variantId) return;

      submitBtn.disabled = true;
      const originalText = submitBtn.innerHTML;
      submitBtn.innerHTML = '<span>ADDING...</span>';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (response.ok) {
          submitBtn.innerHTML = '<span>ADDED ✓</span>';
          submitBtn.style.background = '#00e676';

          // Trigger theme cart drawer or page update
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:cart:updated', { bubbles: true }));
          const cartDrawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
          if (cartDrawer) {
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else if (typeof cartDrawer.showDialog === 'function') {
              cartDrawer.showDialog();
            } else {
              cartDrawer.setAttribute('open', '');
            }
          }
          // Refresh cart count bubble across header
          try {
            const cartData = await fetch('/cart.js').then(r => r.json());
            const countEls = document.querySelectorAll('.cart-count-bubble, [data-cart-count], .cart-bubble');
            countEls.forEach(el => el.textContent = cartData.item_count);
          } catch (err) {}

          setTimeout(() => {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
            submitBtn.style.background = '';
          }, 2000);
        } else {
          submitBtn.innerHTML = '<span>ERROR</span>';
          setTimeout(() => {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
          }, 2000);
        }
      } catch (e) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
      }
    });
  }

  // Initialize on DOM load and section reloads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initFitroxPdp();
      setTimeout(initFitroxPdp, 200);
      setTimeout(initFitroxPdp, 600);
    });
  } else {
    initFitroxPdp();
    setTimeout(initFitroxPdp, 200);
    setTimeout(initFitroxPdp, 600);
  }
  document.addEventListener('shopify:section:load', initFitroxPdp);
})();
